// Every `VITE_*` build variable this app reads, in one schema.
//
// Pure — no `import.meta.env`, no runtime globals — so it can be imported by
// `infra/scripts/env-example.ts` (renders `.env.example`),
// `infra/scripts/env-check.ts` (checks a build's variables), and this app's
// own `env.ts` (validates `import.meta.env` at startup), without any of them
// pulling in Vite's client types. See `@bindery/shared/env`'s header for why
// a registry replaces prose tables here.
//
// All fields are `kind: "build"` (read from `.env.local` at `vite build`
// time) except `VITE_APP_VERSION`, which is `kind: "injected"` —
// `infra/scripts/deploy.ts` sets it from `package.json`, never a developer,
// so the generator and the local check both skip it.
//
// `src/logging/appLogging.ts` and
// `src/logging/earlyErrorCapture.ts` deliberately do NOT read `VITE_LOG_BACKEND` / `VITE_LOG_DEBUG` /
// `VITE_APP_VERSION` through this schema — they keep reading raw
// `import.meta.env` directly. `earlyErrorCapture.ts`'s header explains why: the logger
// must not import a framework before it exists. `earlyErrorCapture.ts` runs
// before the rest of the app's module graph, ahead of anything `env.ts`
// could validate, and `pageBackend.ts` is imported by it. All three are
// still declared here so the manifest generator/checker see them; only these
// files' own reads are exempt. A
// later reader should not "fix" that split.
//
// Because those readers are tolerant by design — an unrecognised backend
// warns and falls back rather than throwing — `env.ts` OMITS the same three
// from its startup parse. Validating a field there that the only reader of it
// deliberately does not validate would make the boot stricter than the
// promise printed on the field, and a trailing-space typo would take the app
// down instead of logging to the console. See `bootSchema` in `env.ts`.
//
// Namespace import, not `import { z }` — zod ships CJS for the node
// condition and the named form resolves to `undefined` under vitest's
// multi-project run (see `packages/shared/src/corpus/selection.ts`).
import * as z from "zod";
import { envField } from "@bindery/shared/env";

export const envSchema = z.object({
  /** The Convex deployment URL. `bunx convex dev` writes it into `.env.local`. */
  VITE_CONVEX_URL: envField(z.url(), {
    kind: "build",
    doc: "The Convex deployment URL this build talks to. `bunx convex dev` writes it into `.env.local`.",
    placeholder: "https://placeholder-not-a-deployment.convex.cloud",
  }),
  /**
   * Logging backend selector. `"axiom"` POSTs canonical events to `/api/log`
   * on the Cloudflare Worker, which forwards them; `"console"` prints them
   * locally; `"both"` fans out to each. Unset means `"console"`. An
   * unrecognised value ALSO falls back to `"console"` — never a throw, since
   * a misconfigured backend must not stop the app from booting — and the app
   * warns once that it ignored what was configured
   * (`src/logging/appLogging.ts`).
   *
   * Read raw by `src/logging/appLogging.ts`, which builds the page's one
   * backend for every Logger, not through `env.ts`, and omitted
   * from `env.ts`'s startup parse so the never-a-throw promise above stays
   * true — see this file's header.
   */
  VITE_LOG_BACKEND: envField(z.enum(["console", "axiom", "both"]).optional(), {
    kind: "build",
    doc: 'Logging backend: "console" (default), "axiom" (POST to /api/log), or "both". An unrecognised value falls back to "console" with a one-time warning, never a throw. Read raw by src/logging/appLogging.ts, not through env.ts.',
    placeholder: "both",
  }),
  /**
   * Gate for `Logger.debug()`. Anything but exactly `"true"` leaves debug
   * off. Read raw by `src/logging/appLogging.ts` and
   * `src/logging/earlyErrorCapture.ts`, not through `env.ts` — see this
   * file's header.
   */
  VITE_LOG_DEBUG: envField(z.string().optional(), {
    kind: "build",
    doc: 'Gate for Logger.debug(). Anything but exactly "true" leaves debug off. Read raw by src/logging/appLogging.ts and src/logging/earlyErrorCapture.ts, not through env.ts.',
    placeholder: "false",
  }),
  /**
   * Public base path for the corpus artifacts, defaulting to `/corpus`. Set
   * it to a CDN origin to serve them elsewhere.
   */
  VITE_CORPUS_BASE: envField(z.string().default("/corpus"), {
    kind: "build",
    doc: "Public base path for the corpus artifacts. Set it to a CDN origin to serve the corpus from somewhere other than the app. Defaults to /corpus.",
    placeholder: "/corpus",
  }),
  /**
   * Application version stamped onto every log event's `version` field. Set
   * by `infra/scripts/deploy.ts` from `package.json` at build time — never by
   * a developer. Read raw by `src/logging/appLogging.ts` and
   * `src/logging/earlyErrorCapture.ts`, not through `env.ts` — see this
   * file's header.
   */
  VITE_APP_VERSION: envField(z.string().optional(), {
    kind: "injected",
    doc: "Application version stamped onto every log event's version field. Set by infra/scripts/deploy.ts from package.json at build time, never by a developer. Read raw by src/logging/appLogging.ts and src/logging/earlyErrorCapture.ts, not through env.ts.",
  }),
});

export type RetellsEnv = z.infer<typeof envSchema>;
